import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { signIn } from '@/lib/auth-client';
import { BUILD_STAMP } from '@/lib/build-info';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mailAvailable, setMailAvailable] = useState(false);

  // Asked before anyone is signed in. It answers only whether the shop has
  // configured outgoing mail, never anything about accounts.
  useEffect(() => {
    let cancelled = false;

    fetch('/api/mail-config/status')
      .then((response) => (response.ok ? response.json() : { available: false }))
      .then((status) => {
        if (!cancelled) setMailAvailable(Boolean(status.available));
      })
      // Falling back to the admin-reset wording is the safe default: it is
      // advice that always works, where a dead link is not.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const { error: signInError } = await signIn.email({ email: email.trim(), password });

      if (signInError) {
        // Vague about credentials, specific about everything else.
        //
        // Not distinguishing "no such account" from "wrong password" is
        // deliberate — it tells an outsider which addresses are real. But
        // reporting *every* failure that way was a mistake that cost hours: a
        // 403 from an untrusted origin is a server misconfiguration, not a bad
        // password, and calling it one sends whoever is locked out to reset a
        // password that was correct all along.
        if (signInError.status === 403) {
          setError(
            'The server refused the sign-in for this web address. It is set up to ' +
              'accept a different one, so no password will work here until that is ' +
              'corrected — see APP_URL and EXTRA_TRUSTED_ORIGINS.',
          );
        } else if (signInError.status === 429) {
          setError('Too many attempts. Wait a minute and try again.');
        } else if (signInError.status >= 500) {
          setError('The server had a problem handling that. Try again in a moment.');
        } else {
          setError('That email and password do not match an account.');
        }
        return;
      }

      // Full reload rather than client navigation, so every query starts fresh
      // against the new session instead of reusing the signed-out cache.
      window.location.href = '/';
    } catch {
      setError('Could not reach the server. Check that the app is running.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ShipSwift</h1>
          <p className="text-sm text-slate-500">Gold Star Printing</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 space-y-4"
        >
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 h-11"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>

          {/*
            The link appears only when outgoing mail is configured. Offering a
            reset that silently sends nothing is worse than not offering one:
            the person waits for an email that will never arrive instead of
            asking someone who could actually help.
          */}
          {mailAvailable ? (
            <p className="text-sm text-center pt-2">
              <Link to="/forgot-password" className="text-blue-600 hover:text-blue-800">
                Forgot your password?
              </Link>
            </p>
          ) : (
            <p className="text-xs text-center text-slate-500 pt-2">
              Forgot your password? An administrator can reset it for you.
            </p>
          )}
        </form>

        <p className="text-xs text-center text-slate-400 mt-6">{BUILD_STAMP}</p>
      </div>
    </div>
  );
}
